import {
  IAnnotation,
  IRIA,
  IRect,
  IGeometry,
  IData,
  // ISelection
} from "@services/dashboardService/types";
import { Box } from "@mui/material";
import placeholderImg from "@images/placeholder.png";
import { getLabelsV2 } from "@services/projectService";
const { v4: uuidv4 } = require("uuid");

export interface ProjectLabel {
  name: string;
  color: string;
}

export const labelsFromContextState = (
  labels?: string[],
  colors?: string[]
): ProjectLabel[] =>
  labels?.map((label, index) => ({
    name: label,
    color: colors?.[index] || "#000000",
  })) ?? [];

export const normalizeLabelsV2Response = (resp: any): ProjectLabel[] => {
  const rawApiData = resp?.labels ?? resp;
  const classes = rawApiData?.classes ?? resp?.classes ?? [];
  const colors = rawApiData?.colors ?? resp?.colors ?? [];

  return classes.map((name: string, index: number) => ({
    name,
    color: colors?.[index] ?? "#000000",
  }));
};

export const fetchProjectLabels = async (
  projectId: string | number,
  projectType?: string,
  fallbackLabels?: ProjectLabel[]
): Promise<ProjectLabel[]> => {
  try {
    const resp = await getLabelsV2(projectId, projectType);
    const normalized = normalizeLabelsV2Response(resp);
    if (normalized.length > 0) {
      console.log("Loaded labels:", normalized.length);
      return normalized;
    }
  } catch (e) {
    console.error("Error fetching project labels:", e);
  }
  return fallbackLabels ?? [];
};

export const ria_to_annotation = (
  riaList: IRIA[],
  labels: string[],
  oImageSize: IRect,
  imageId: string,
  officialImageSize: IRect
) => {
  //percent to absolute
  console.log("rialist===>", riaList);

  let class_ids: number[] = [];
  let bboxes: any[] = [];

  let _bbox: number[];
  let _class_id: number;
  for (let ria of riaList) {
    _bbox = [
      (ria.geometry.x * oImageSize.width) / 100,
      (ria.geometry.y * oImageSize.height) / 100,
      (ria.geometry.width * oImageSize.width) / 100,
      (ria.geometry.height * oImageSize.height) / 100,
    ];
    bboxes.push(_bbox);
    _class_id = labels.indexOf(ria.data.text);
    class_ids.push(_class_id);
  }

  let annotation: IAnnotation = {
    image_id: imageId,
    bboxes,
    class_ids,
    // size: [oImageSize.height, oImageSize.width]
    size: [officialImageSize.width, officialImageSize.height],
    target_size: [oImageSize.width, oImageSize.height],
  };
  return annotation;
};

export const annotation_to_ria = (
  bboxes: number[][],
  class_ids: number[],
  oImageSize: IRect,
  labels: string[]
) => {
  //percent to absolute

  const len = bboxes?.length ?? 0;
  let riaList: IRIA[] = [];
  if (!len) return [];
  let geometry: IGeometry;
  let data: IData;
  // let selection: ISelection = {anchorX: 0, anchorY: 0, mode: 'EDITING', showEditor: true}
  let bbox: number[] = [];

  for (let i = 0; i < len; i++) {
    bbox = bboxes[i];
    geometry = { type: "RECTANGLE", x: 0, y: 0, width: 0, height: 0 };
    data = { text: "" };

    geometry.x = (bbox[0] / oImageSize.width) * 100;
    geometry.y = (bbox[1] / oImageSize.height) * 100;
    geometry.width = (bbox[2] / oImageSize.width) * 100;
    geometry.height = (bbox[3] / oImageSize.height) * 100;

    // selection.anchorX = bbox[0] / oImageSize.width * 100 ?? -1;
    // selection.anchorY = bbox[1] / oImageSize.height * 100 ?? -1;

    data.text = labels[class_ids[i]] ?? "";
    data.id = uuidv4();
    riaList.push({
      geometry,
      data,
    });
  }

  console.log("======> riaList", riaList);
  return riaList;
};

interface CustomImagePlaceholderProps {
  size: IRect;
}

export function CustomImagePlaceholder({ size }: CustomImagePlaceholderProps) {
  console.log("seize", size);
  let width = !!size.width ? size.width : "100%";
  let height = !!size.height ? size.height : "auto";

  console.log("width, height", width, height);
  return (
    <Box
      component="img"
      sx={{
        width: width,
        height: height,
      }}
      alt="placeholder"
      src={placeholderImg}
    />
  );
}

export function RenderTemplate(props: any) {
  const { children, geometry } = props;

  console.log();
  if (!geometry) return <>{children}</>;

  return (
    <div
      style={{
        color: "black",
        padding: 10,
        fontSize: 12,
        background: "white",
        borderRadius: 3,
        position: "absolute",
        left: `${geometry.x}%`,
        top: `${geometry.y + geometry.height}%`,
        display: "flex",
        justifyContent: "center",
        flexDirection: "row",
        zIndex: 2,
      }}
    >
      {children}
    </div>
  );
}

interface Point {
  x: number;
  y: number;
}

interface Polygon {
  points: Point[];
  label: string;
  color: string;
}

interface InputData {
  [key: string]: {
    polygons: Polygon[];
    activePolygon: any[];
  };
}

interface OutputPolygon {
  points: Point[];
  label: string;
  color: string;
}

interface OutputData {
  [key: string]: OutputPolygon[];
}

export const convert_to_nifit_file_reader_data = (data: InputData): OutputData => {
  const output: OutputData = {};

  // Iterate through each slice/frame
  Object.entries(data).forEach(([key, value]) => {
    // Skip if no polygons
    if (!value.polygons || value.polygons.length === 0) {
      return;
    }

    // Convert each polygon
    const polygons = value.polygons.map(polygon => ({
      points: polygon.points.map(point => ({
        x: Math.round(point.x), // Round to nearest integer
        y: Math.round(point.y)  // Round to nearest integer
      })),
      label: polygon.label, // Convert label to lowercase
      color: polygon.color
    }));

    output[key] = polygons;
  });

  return output;
};

/**
 * Axial display matches matplotlib `imshow(data[:, :, z])` (origin='upper'):
 *   canvas width  = ny = dims[2]
 *   canvas height = nx = dims[1]
 *   pixel (x, y)  = voxel (i=y, j=x, k=z)
 *
 * Annotation / API (x, y) are imshow pixel coords — same as canvas — so these
 * helpers are identity. Kept for call-site clarity / backward-compatible names.
 */
export const normalizePoint = (point: any): Point => {
  if (Array.isArray(point)) {
    return { x: Number(point[0]) || 0, y: Number(point[1]) || 0 };
  }
  return { x: Number(point?.x) || 0, y: Number(point?.y) || 0 };
};

export const niftiPointToDisplay = (
  point: Point,
  _nx?: number,
  _ny?: number
): Point => normalizePoint(point);

export const displayPointToNifti = (
  point: Point,
  _nx?: number,
  _ny?: number
): Point => normalizePoint(point);

/** Flat index in NIfTI storage order (C-order / nifti-reader-js). */
export const sampleVoxel = (
  data: Float32Array,
  dims: [number, number, number],
  i: number,
  j: number,
  k: number
): number => {
  const [nx, ny] = dims;
  return data[i + j * nx + k * nx * ny];
};

/**
 * Cast raw NIfTI image bytes to Float32 and apply scl_slope / scl_inter.
 * Does not apply affine / RAS reorientation (storage order only).
 */
export const volumeFromNiftiImage = (
  imageBuffer: ArrayBuffer,
  datatypeCode: number,
  sclSlope: number,
  sclInter: number
): Float32Array => {
  let raw: ArrayLike<number>;
  switch (datatypeCode) {
    case 2: // UINT8
      raw = new Uint8Array(imageBuffer);
      break;
    case 4: // INT16
      raw = new Int16Array(imageBuffer);
      break;
    case 8: // INT32
      raw = new Int32Array(imageBuffer);
      break;
    case 16: // FLOAT32
      raw = new Float32Array(imageBuffer);
      break;
    case 64: // FLOAT64
      raw = new Float64Array(imageBuffer);
      break;
    case 256: // INT8
      raw = new Int8Array(imageBuffer);
      break;
    case 512: // UINT16
      raw = new Uint16Array(imageBuffer);
      break;
    case 768: // UINT32
      raw = new Uint32Array(imageBuffer);
      break;
    default:
      raw = new Float32Array(imageBuffer);
      break;
  }

  const slope =
    !sclSlope || !Number.isFinite(sclSlope) ? 1 : sclSlope;
  const inter = !Number.isFinite(sclInter) ? 0 : sclInter;

  const out = new Float32Array(raw.length);
  if (slope === 1 && inter === 0 && raw instanceof Float32Array) {
    out.set(raw);
    return out;
  }
  for (let i = 0; i < raw.length; i++) {
    out[i] = raw[i] * slope + inter;
  }
  return out;
};

export const mapPolygonsNiftiToDisplay = (
  polygons: Polygon[],
  nx: number,
  ny: number
): Polygon[] =>
  polygons.map((polygon) => ({
    ...polygon,
    points: (polygon.points || []).map((p) => niftiPointToDisplay(p, nx, ny)),
  }));

export const mapPolygonsDisplayToNifti = (
  polygons: Polygon[],
  nx: number,
  ny: number
): Polygon[] =>
  polygons.map((polygon) => ({
    ...polygon,
    points: (polygon.points || []).map((p) => displayPointToNifti(p, nx, ny)),
  }));

export const mapSegmentationDisplayToNifti = (
  data: InputData,
  nx: number,
  ny: number
): InputData => {
  const output: InputData = {};
  Object.entries(data).forEach(([key, value]) => {
    output[key] = {
      polygons: mapPolygonsDisplayToNifti(value.polygons || [], nx, ny),
      activePolygon: (value.activePolygon || []).map((p: Point) =>
        displayPointToNifti(p, nx, ny)
      ),
    };
  });
  return output;
};